/**
 * Live quotes: one stream, one store, every price on the page.
 *
 * The API pushes bitcoin's mid and each subscribed collection's fair value
 * over server-sent events (`/quotes/stream`). This module holds the latest
 * of each, hands them to React through `useSyncExternalStore`, and keeps a
 * single connection open per page however many components read from it.
 *
 * A component that needs a collection's mark registers interest; the set of
 * interests is what the stream subscribes to. Interest is reference-counted
 * so a component unmounting drops its collections and the stream narrows.
 *
 * When EventSource cannot connect (an old proxy, a browser without it, a
 * deploy in progress) the store falls back to polling the snapshot every
 * fifteen seconds and keeps trying the stream in the background.
 */

import { API_URL } from '@/utils/constants'

export interface BtcTick {
	usd: number
	ts: number
}

export interface MarkQuote {
	slug: string
	fair_sats: number
	p10_sats: number
	p90_sats: number
	samples: number
	/** 7d change of the band's level, percent; null until the API holds a week. */
	change_week?: number | null
	ts: number
}

export interface QuoteState {
	btc: BtcTick | null
	marks: Readonly<Record<string, MarkQuote>>
	/** 'live' on an open stream, 'polling' on the fallback, 'idle' before either. */
	transport: 'idle' | 'live' | 'polling'
}

const QUOTES_URL = `${API_URL}/quotes`
const RECONNECT_MIN_MS = 1_000
const RECONNECT_MAX_MS = 30_000
const POLL_MS = 15_000
/** A resumed or buffered stream may deliver thousands of ticks in one burst. */
const NOTIFY_MS = 100
/** Interest changes are batched so a page mounting twenty rows opens one stream. */
const RESUBSCRIBE_DEBOUNCE_MS = 250

let state: QuoteState = { btc: null, marks: {}, transport: 'idle' }
const listeners = new Set<() => void>()
const interest = new Map<string, number>()

let source: EventSource | null = null
let sourceSlugs = ''
let reconnectDelay = RECONNECT_MIN_MS
let reconnectTimer: number | null = null
let resubscribeTimer: number | null = null
let pollTimer: number | null = null
let failures = 0
let notifyTimer: number | null = null

function emit(next: QuoteState) {
	state = next
	if (notifyTimer !== null || listeners.size === 0) return
	// Keep the newest snapshot immediately, but render at most once per batch.
	// useSyncExternalStore updates are synchronous; notifying per SSE frame
	// makes a replay block every price consumer for seconds.
	notifyTimer = window.setTimeout(() => {
		notifyTimer = null
		for (const listener of [...listeners]) listener()
	}, NOTIFY_MS)
}

function applyBtc(tick: BtcTick) {
	if (!tick || !Number.isFinite(tick.usd) || tick.usd <= 0 || !Number.isFinite(tick.ts)) return
	const prev = state.btc
	if (prev && (tick.ts < prev.ts || (tick.ts === prev.ts && tick.usd === prev.usd))) return
	emit({ ...state, btc: tick })
}

function applyMark(mark: MarkQuote) {
	if (
		!mark ||
		!mark.slug ||
		!Number.isFinite(mark.fair_sats) ||
		mark.fair_sats <= 0 ||
		!Number.isFinite(mark.ts)
	)
		return
	const prev = state.marks[mark.slug]
	if (
		prev &&
		(mark.ts < prev.ts ||
			(mark.ts === prev.ts &&
				mark.fair_sats === prev.fair_sats &&
				mark.p10_sats === prev.p10_sats &&
				mark.p90_sats === prev.p90_sats &&
				mark.samples === prev.samples &&
				mark.change_week === prev.change_week))
	)
		return
	emit({ ...state, marks: { ...state.marks, [mark.slug]: mark } })
}

function applySnapshot(snap: { btc?: BtcTick | null; marks?: MarkQuote[] }) {
	if (snap.btc) applyBtc(snap.btc)
	if (Array.isArray(snap.marks)) {
		for (const mark of snap.marks) applyMark(mark)
	}
}

function slugsParam(): string {
	return [...interest.keys()].sort().join(',')
}

function stopPolling() {
	if (pollTimer !== null) {
		window.clearInterval(pollTimer)
		pollTimer = null
	}
}

async function pollOnce() {
	try {
		const res = await fetch(`${QUOTES_URL}?collections=${encodeURIComponent(slugsParam())}`, {
			cache: 'no-store'
		})
		if (res.ok) {
			applySnapshot(await res.json())
		}
	} catch {
		// The next tick tries again.
	}
}

function startPolling() {
	if (pollTimer !== null) {
		return
	}
	emit({ ...state, transport: 'polling' })
	void pollOnce()
	pollTimer = window.setInterval(() => void pollOnce(), POLL_MS)
}

function closeSource() {
	if (source) {
		source.close()
		source = null
	}
	if (reconnectTimer !== null) {
		window.clearTimeout(reconnectTimer)
		reconnectTimer = null
	}
}

function openSource() {
	if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
		startPolling()
		return
	}
	closeSource()
	sourceSlugs = slugsParam()
	// EventSource has no cache option. A unique URL avoids a cached stream
	// replaying hours of historical ticks on each new connection.
	const es = new EventSource(
		`${QUOTES_URL}/stream?collections=${encodeURIComponent(
			sourceSlugs
		)}&connection=${crypto.randomUUID()}`
	)
	source = es
	es.addEventListener('snapshot', (e) => {
		failures = 0
		reconnectDelay = RECONNECT_MIN_MS
		stopPolling()
		if (state.transport !== 'live') {
			emit({ ...state, transport: 'live' })
		}
		try {
			applySnapshot(JSON.parse((e as MessageEvent).data))
		} catch {
			// A malformed snapshot is not fatal; ticks still follow.
		}
	})
	es.addEventListener('btc', (e) => {
		try {
			applyBtc(JSON.parse((e as MessageEvent).data))
		} catch {
			// Ignore a bad frame.
		}
	})
	es.addEventListener('mark', (e) => {
		try {
			applyMark(JSON.parse((e as MessageEvent).data))
		} catch {
			// Ignore a bad frame.
		}
	})
	es.onerror = () => {
		// EventSource reconnects on its own for transient drops; we take
		// over only so the backoff is ours and polling covers the gap.
		es.close()
		if (source === es) {
			source = null
		}
		failures += 1
		if (failures >= 2) {
			startPolling()
		}
		reconnectTimer = window.setTimeout(() => {
			reconnectTimer = null
			if (listeners.size > 0) {
				openSource()
			}
		}, reconnectDelay)
		reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS)
	}
}

/** Reopen the stream with the current interest set, debounced. */
function resubscribe() {
	if (resubscribeTimer !== null) {
		return
	}
	resubscribeTimer = window.setTimeout(() => {
		resubscribeTimer = null
		if (listeners.size === 0) {
			return
		}
		if (!source || slugsParam() !== sourceSlugs) {
			openSource()
		}
	}, RESUBSCRIBE_DEBOUNCE_MS)
}

export function subscribe(listener: () => void): () => void {
	listeners.add(listener)
	if (listeners.size === 1) {
		resubscribe()
	}
	return () => {
		listeners.delete(listener)
		if (listeners.size === 0) {
			if (notifyTimer !== null) {
				window.clearTimeout(notifyTimer)
				notifyTimer = null
			}
			closeSource()
			stopPolling()
			emit({ ...state, transport: 'idle' })
		}
	}
}

export function getSnapshot(): QuoteState {
	return state
}

const SERVER_SNAPSHOT: QuoteState = { btc: null, marks: {}, transport: 'idle' }
export function getServerSnapshot(): QuoteState {
	return SERVER_SNAPSHOT
}

/** Hold interest in a set of collections; returns the release. */
export function want(slugs: readonly string[]): () => void {
	const held = slugs.filter(Boolean)
	for (const s of held) {
		interest.set(s, (interest.get(s) ?? 0) + 1)
	}
	if (held.length > 0) {
		resubscribe()
	}
	return () => {
		for (const s of held) {
			const n = (interest.get(s) ?? 1) - 1
			if (n <= 0) {
				interest.delete(s)
			} else {
				interest.set(s, n)
			}
		}
		if (held.length > 0) {
			resubscribe()
		}
	}
}
